import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { DateRangePicker, toaster } from 'rsuite'
import {
  IFText,
  IFSkeleton,
  IFToastMessage,
  AnalyticsDataCard,
  ChartComponent,
} from 'Components'
import { Colors } from 'Theme'
import RequestState from 'Enums/RequestState'
import { formatNumber, getEnergyUnit } from 'Utils/Calculations'
import CardSelectors from 'Stores/Card/Selectors'
import CardActions from 'Stores/Card/Actions'
import AuthSelectors from 'Stores/Auth/Selectors'
import CardUsageStatsSkeleton from './CardUsageStatsSkeleton'
import Styles from './CardUsageStats.module.css'

const DEFAULT_RANGE_DAYS = 30
// Longest run of 3 consecutive calendar months is 92 days; cap the window there
// (matches the backend) so a single token's aggregation stays bounded.
const MAX_RANGE_DAYS = 92
const MAX_RANGE_MS = MAX_RANGE_DAYS * 24 * 60 * 60 * 1000

const buildDefaultRange = () => {
  const end = new Date()
  end.setHours(23, 59, 59, 999)
  const start = new Date()
  start.setDate(start.getDate() - DEFAULT_RANGE_DAYS)
  start.setHours(0, 0, 0, 0)
  return [start, end]
}

const startOfDay = (date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}
const endOfDay = (date) => {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

const CardUsageStats = ({
  selectedCard,
  cardTransactionStats,
  fetchCardTransactionStatsRequestState,
  fetchCardRequestState,
  fetchCardListRequestState,
  operator,
  fetchCardTransactionStats,
  clearCardTransactionStats,
}) => {
  const { t } = useTranslation()
  const [range, setRange] = useState(buildDefaultRange)
  const [from, to] = range

  const cardId = selectedCard?._id

  useEffect(() => {
    if (!cardId) return
    fetchCardTransactionStats(
      cardId,
      from.toISOString(),
      to.toISOString(),
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    )
  }, [cardId, from, to])

  useEffect(() => {
    return () => {
      clearCardTransactionStats()
    }
  }, [])

  // The card itself loading (initial page load / token switch) must also drive
  // the skeleton, otherwise on refresh this block stays blank while the rest of
  // the details panel shows skeletons.
  const cardIsLoading =
    fetchCardListRequestState === RequestState.LOADING ||
    fetchCardRequestState === RequestState.LOADING
  const isLoading =
    cardIsLoading ||
    fetchCardTransactionStatsRequestState === RequestState.LOADING ||
    fetchCardTransactionStatsRequestState === RequestState.UNINITIALIZED
  // While the card is (re)loading or not resolved yet, the header controls have
  // no meaningful card to act on, so skeleton them too.
  const headerLoading = cardIsLoading || !selectedCard
  const isError =
    fetchCardTransactionStatsRequestState !== RequestState.LOADING &&
    fetchCardTransactionStatsRequestState !== RequestState.UNINITIALIZED &&
    fetchCardTransactionStatsRequestState !== RequestState.SUCCEEDED

  const totalEnergyWh = cardTransactionStats?.totalEnergy || 0
  const totalCost = cardTransactionStats?.totalCost || 0
  const sessionCount = cardTransactionStats?.sessionCount || 0
  const series = useMemo(
    () => cardTransactionStats?.series || [],
    [cardTransactionStats],
  )
  const granularity = cardTransactionStats?.granularity || 'day'
  const currencyCode =
    cardTransactionStats?.currency?.code ||
    operator?.country?.currency?.code ||
    ''

  const { divisor: totalDivisor, unit: totalUnit } = getEnergyUnit(totalEnergyWh)
  const totalEnergyValue =
    totalEnergyWh > 0 ? Number((totalEnergyWh / totalDivisor).toFixed(1)) : 0

  // The chart uses its own unit derived from the largest bucket so bars stay
  // readable even when the running total sits in a different unit band.
  const maxBucketWh = series.reduce((max, b) => Math.max(max, b.energy || 0), 0)
  const { divisor: chartDivisor, unit: chartUnit } = getEnergyUnit(maxBucketWh)

  const chartSeries = useMemo(
    () => [
      {
        name: t('CardDetails.Energy'),
        data: series.map((bucket) =>
          Number(((bucket.energy || 0) / chartDivisor).toFixed(2)),
        ),
      },
    ],
    [series, chartDivisor, t],
  )

  const chartOptions = useMemo(
    () => ({
      chart: {
        id: 'CardUsageEnergyChart',
        type: 'bar',
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'ProximaNova',
      },
      colors: [Colors.primary],
      plotOptions: {
        bar: { borderRadius: 4, columnWidth: series.length > 20 ? '85%' : '55%' },
      },
      dataLabels: { enabled: false },
      grid: { borderColor: Colors.Analytics.AnalyticsDivider },
      xaxis: {
        categories: series.map((bucket) =>
          moment(bucket.date).format(
            granularity === 'month' ? 'MMM YYYY' : 'DD MMM',
          ),
        ),
        labels: {
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          style: { fontFamily: 'ProximaNova', fontSize: '0.7rem' },
        },
        tickPlacement: 'on',
      },
      yaxis: {
        labels: {
          style: { fontFamily: 'ProximaNova', fontSize: '0.7rem' },
          formatter: (val) => `${Math.round(val)}`,
        },
        title: {
          text: chartUnit,
          style: { fontFamily: 'ProximaNova', fontWeight: 500 },
        },
      },
      tooltip: {
        theme: 'dark',
        y: { formatter: (val) => `${val} ${chartUnit}` },
      },
      states: { hover: { filter: { type: 'lighten', value: 0.1 } } },
    }),
    [series, granularity, chartUnit],
  )

  const predefinedRanges = useMemo(
    () => [
      {
        label: t('CardDetails.Last7Days'),
        value: [startOfDay(moment().subtract(6, 'days').toDate()), endOfDay(new Date())],
      },
      {
        label: t('CardDetails.Last30Days'),
        value: [startOfDay(moment().subtract(29, 'days').toDate()), endOfDay(new Date())],
      },
      {
        label: t('CardDetails.Last90Days'),
        value: [startOfDay(moment().subtract(89, 'days').toDate()), endOfDay(new Date())],
      },
      {
        label: t('CardDetails.ThisMonth'),
        value: [startOfDay(moment().startOf('month').toDate()), endOfDay(new Date())],
      },
    ],
    [t],
  )

  // Times applied to days picked from the calendar when the user doesn't edit
  // the time fields: whole-day start / end.
  const calendarDefault = useMemo(
    () => [startOfDay(new Date()), endOfDay(new Date())],
    [],
  )

  if (!selectedCard && !cardIsLoading) return null

  const showEmpty = !isLoading && !isError && sessionCount === 0

  return (
    <div className={Styles.Container} style={{ borderColor: Colors.grey }}>
      <div className={Styles.Header}>
        {headerLoading ? (
          <IFSkeleton variant="text" width={90} height={22} />
        ) : (
          <IFText className={Styles.Title} style={{ color: Colors.text }}>
            {t('CardDetails.UsageStats')}
          </IFText>
        )}
        <div className={Styles.RangePickerWrapper}>
          {headerLoading ? (
            <IFSkeleton
              variant="rectangular"
              width={210}
              height={32}
              style={{ borderRadius: 6 }}
            />
          ) : (
            <DateRangePicker
              size="sm"
              cleanable={false}
              placement="bottomEnd"
              format="dd MMM yyyy HH:mm"
              character=" – "
              value={range}
              ranges={predefinedRanges}
              defaultCalendarValue={calendarDefault}
              disabledDate={(date, selectDate, selectedDone) => {
                if (date > new Date()) return true
                // While picking the second endpoint, block dates further than
                // the cap from the first pick so the range can't exceed 3 months.
                if (!selectedDone && selectDate && selectDate.length > 0) {
                  const anchor = selectDate[0]
                  if (
                    anchor &&
                    Math.abs(date.getTime() - anchor.getTime()) > MAX_RANGE_MS
                  )
                    return true
                }
                return false
              }}
              onChange={(value) => {
                if (!value || value.length !== 2) return
                if (
                  Math.abs(value[1].getTime() - value[0].getTime()) >
                  MAX_RANGE_MS
                ) {
                  toaster.push(
                    <IFToastMessage
                      type="warning"
                      text={t('CardDetails.MaxRangeWarning')}
                    />,
                    { placement: 'topEnd' },
                  )
                  return
                }
                setRange([value[0], value[1]])
              }}
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <CardUsageStatsSkeleton />
      ) : (
        <>
          <div className={Styles.Tiles}>
            <AnalyticsDataCard
              size="small"
              title={t('CardDetails.Energy')}
              primaryValue={formatNumber(totalEnergyValue)}
              unit={totalUnit}
              showDash={isError}
            />
            <AnalyticsDataCard
              size="small"
              title={t('CardDetails.Cost')}
              primaryValue={formatNumber(totalCost)}
              unit={currencyCode}
              showDash={isError}
            />
            <AnalyticsDataCard
              size="small"
              title={t('CardDetails.Sessions')}
              primaryValue={formatNumber(sessionCount)}
              showDash={isError}
            />
          </div>

          <div className={Styles.ChartContainer}>
            {showEmpty ? (
              <div className={Styles.EmptyState}>
                <IFText style={{ color: Colors.gray }}>
                  {t('CardDetails.NoUsageData')}
                </IFText>
              </div>
            ) : isError ? (
              <div className={Styles.EmptyState}>
                <IFText style={{ color: Colors.gray }}>
                  {t('CardDetails.UsageStatsError')}
                </IFText>
              </div>
            ) : (
              <ChartComponent
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={160}
                width="100%"
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  selectedCard: CardSelectors.getSelectedCard(state),
  cardTransactionStats: CardSelectors.getCardTransactionStats(state),
  fetchCardTransactionStatsRequestState:
    CardSelectors.getFetchCardTransactionStatsRequestState(state),
  fetchCardRequestState: CardSelectors.getFetchCardRequestState(state),
  fetchCardListRequestState: CardSelectors.getFetchCardListRequestState(state),
  operator: AuthSelectors.getOperator(state),
})

function mapDispatchToProps(dispatch) {
  return {
    fetchCardTransactionStats: (cardId, from, to, timezone) =>
      dispatch(
        CardActions.fetchCardTransactionStats(cardId, from, to, timezone),
      ),
    clearCardTransactionStats: () =>
      dispatch(CardActions.clearCardTransactionStats()),
  }
}

CardUsageStats.propTypes = {
  selectedCard: PropTypes.object,
  cardTransactionStats: PropTypes.object,
  fetchCardTransactionStatsRequestState: PropTypes.number,
  fetchCardRequestState: PropTypes.number,
  fetchCardListRequestState: PropTypes.number,
  operator: PropTypes.object,
  fetchCardTransactionStats: PropTypes.func,
  clearCardTransactionStats: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(CardUsageStats)
